import { useContext, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { FormattedMessage } from 'react-intl'
import classnames from 'classnames'
import {
  AmplitudeContext,
  AmplitudeEventConstants,
} from '@homebot/shared-util-quartermaster'

// Actions
import { updateHomeInsurancePolicy } from '../../../../actions/home'

// Components
import {
  BotHeadSpin,
  Button,
  Form,
  FormField,
  If,
  Page,
  Text,
  Unless,
} from '../../..'
import { SlideOnMount } from '../../../shared/Animations'
import { RedirectToHomeDigest } from '../../../shared/Navigation'
import { ActiveOverlayContext } from '../../../shared/Routing/OverlayContext'

// Hooks
import { useHomeInsurance } from '../../../../hooks'

// Selectors
import {
  selectHomeUuid,
  selectHomeSoldDate,
} from '../../../../store/selectors/home'
import { selectInsurancePolicyStartDate } from '../../../../store/selectors/homeInsurance'

// Utils
import { handleKeyNavForward } from '../../../../helpers/input'
import {
  HomeInsuranceSaveClick,
  HomeInsuranceCancelClick,
  AdjustedHomeInsuranceDate,
} from '../../../../constants/trackingEvents'
import { dispatchEvent } from '../../../../helpers/tagManager'

// Assets
import checklistImg from './checklist.gif'

// Styles
import styles from './YourInsurance.module.scss'

const daysInMonth = month => {
  const currentDate = new Date()
  const currentYear = currentDate.getFullYear()
  const days = new Date(currentYear, month, 0).getDate()

  return days
}

const YourInsurance = () => {
  const { ampli } = useContext(AmplitudeContext)
  const dispatch = useDispatch()
  const { doNavBack } = useContext(ActiveOverlayContext)
  const { loading, shouldRender } = useHomeInsurance()
  const homeUuid = useSelector(selectHomeUuid)
  const homeSoldDate = useSelector(selectHomeSoldDate)
  const defaultDate =
    useSelector(selectInsurancePolicyStartDate) || homeSoldDate

  const getDefaultDate = () => {
    const date = new Date(defaultDate)

    return {
      defaultMonth: date.getMonth(),
      defaultDay: date.getDate(),
      defaultYear: date.getFullYear(),
    }
  }

  // this offset is required because the Date object in javascript
  // starts at 0 (January), however our daysInMonth function expects the
  // month to be 1..12 therefore offset is required to get the correct
  // calculation
  const offSetFormData = m => Number(m) + 1

  const { defaultMonth, defaultDay, defaultYear } = getDefaultDate()
  const [daysInSelectedMonth, setDaysInSelectedMonth] = useState(() =>
    daysInMonth(offSetFormData(defaultMonth))
  )

  const handleOnMonthChange = data => {
    // unexpected behavior, data gets bubbled up
    // rather than the event itself
    const days = daysInMonth(offSetFormData(data))
    setDaysInSelectedMonth(days)
  }

  // Even though daysInSelectedMonth is the correct number
  // when you pass the correct range to the form the options
  // are incorrectly rendered. The offsetting is a hack to adjust
  // for the unexpected behavior.
  const daysOptions = () =>
    Array(daysInSelectedMonth)
      .fill()
      .map((_, i) => ({ key: i, value: i + 1 }))

  const handleSave = data => {
    const { month, day, year } = data

    const attrs = {
      insurancePolicyStartDate: `${year}-${offSetFormData(
        month
      )}-${offSetFormData(day)}`,
    }

    if (defaultDate !== attrs.insurancePolicyStartDate) {
      dispatchEvent(AdjustedHomeInsuranceDate)
    }

    dispatch(updateHomeInsurancePolicy({ doNavBack, homeUuid, attrs }))
  }

  const handleCancel = e => {
    e.preventDefault()
    dispatchEvent(HomeInsuranceCancelClick)
    doNavBack()
  }

  if (!loading && !shouldRender) {
    return <RedirectToHomeDigest />
  }

  return (
    <Page>
      <Page.Body className={styles.body}>
        <If condition={loading}>
          <BotHeadSpin panelSpacing />
        </If>
        <Unless condition={loading}>
          <SlideOnMount>
            <img
              className={styles.checklist}
              src={checklistImg}
              alt='checklist'
            />
            <Text
              element='div'
              className={styles.header}
              variant='neutralCool'
              variantCount='900'
            >
              <FormattedMessage
                id='HomeInsurance.yourInsurance.header'
                defaultMessage='What’s your best guess for when your current policy started?'
              />
            </Text>
          </SlideOnMount>

          <SlideOnMount>
            <Text
              element='div'
              className={styles.subheader}
              variant='neutralCool'
              variantCount='900'
            >
              <FormattedMessage
                id='HomeInsurance.yourInsurance.subheader'
                defaultMessage='We’ll use this date to send you a reminder.'
              />
            </Text>
            <Form
              fields={['month', 'day', 'year']}
              onSubmit={(e, data) => {
                e.preventDefault()
                ampli?.insuranceReviewed({
                  insurance_policy_checked:
                    AmplitudeEventConstants.ADJUST_HOME_INSURANCE_POLICY_START_DATE,
                })
                handleSave(data)
              }}
            >
              <div className={styles.formRow}>
                <FormField.Month
                  className={classnames(styles.input, styles.monthInput)}
                  onChange={handleOnMonthChange}
                  defaultValue={defaultMonth}
                />
                <FormField.Day
                  className={classnames(styles.input, styles.dayInput)}
                  options={daysOptions()}
                  defaultValue={defaultDay}
                />
                <FormField.Year
                  className={classnames(styles.input, styles.yearInput)}
                  defaultValue={defaultYear}
                  required
                />
              </div>
              <Button
                primary
                type='submit'
                track={HomeInsuranceSaveClick}
                className={styles.saveBtn}
                data-qa='save-policy-date'
              >
                <FormattedMessage
                  id='HomeInsurance.UpdatePolicyDate.saveButton'
                  defaultMessage='Save'
                />
              </Button>
              <Button
                className={styles.cancel}
                onClick={handleCancel}
                onKeyDown={handleKeyNavForward(handleCancel)}
                role='button'
                tabIndex='0'
                minimal
              >
                <FormattedMessage
                  id='HomeInsurance.UpdatePolicyDate.cancelButton'
                  defaultMessage='Cancel'
                />
              </Button>
            </Form>
          </SlideOnMount>
        </Unless>
      </Page.Body>
    </Page>
  )
}

export default YourInsurance
